// User Settings — RTM Req 48-52
// Personal settings page accessible to every authenticated user.
// Tabs: Profile | Security | Sessions | Preferences
import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageShell } from "@/components/page-shell";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import QRCode from "react-qr-code";
import {
  User, Lock, Smartphone, Bell, ShieldCheck, ShieldAlert,
  RefreshCw, LogOut, KeyRound, CheckCircle2, AlertCircle, Copy,
  Monitor, Trash2, Globe,
} from "lucide-react";

type EmailEvents = {
  tickets?: boolean;
  approvals?: boolean;
  liveChat?: boolean;
  billing?: boolean;
  security?: boolean;
};

type UserProfile = {
  id: string;
  fullName: string;
  userName: string;
  email: string;
  jobTitle?: string;
  avatarUrl?: string;
  timezone?: string;
  locale?: string;
  mfaEnabled: boolean;
  preferences?: {
    theme?: string;
    density?: string;
    emailNotifications?: boolean;
    browserNotifications?: boolean;
    emailEvents?: EmailEvents;
  };
};

type Session = {
  id: string;
  deviceInfo?: string;
  ipAddress?: string;
  lastSeenAt: string;
  createdAt: string;
  isCurrent: boolean;
};

// ── Timezones ─────────────────────────────────────────────────────────────────
// Use the browser's built-in IANA timezone list when available; otherwise fall
// back to a curated common subset so the selector always renders.
const COMMON_TZ = [
  "UTC",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "Pacific/Honolulu",
  "America/Toronto", "America/Vancouver", "America/Mexico_City",
  "America/Sao_Paulo", "America/Buenos_Aires", "America/Bogota",
  "Europe/London", "Europe/Dublin", "Europe/Lisbon",
  "Europe/Paris", "Europe/Berlin", "Europe/Rome", "Europe/Madrid",
  "Europe/Amsterdam", "Europe/Brussels", "Europe/Vienna", "Europe/Zurich",
  "Europe/Warsaw", "Europe/Prague", "Europe/Budapest",
  "Europe/Stockholm", "Europe/Helsinki", "Europe/Oslo", "Europe/Copenhagen",
  "Europe/Bucharest", "Europe/Athens",
  "Europe/Moscow", "Europe/Istanbul",
  "Asia/Dubai", "Asia/Riyadh", "Asia/Karachi",
  "Asia/Kolkata", "Asia/Dhaka", "Asia/Colombo",
  "Asia/Bangkok", "Asia/Ho_Chi_Minh", "Asia/Jakarta",
  "Asia/Singapore", "Asia/Kuala_Lumpur", "Asia/Shanghai", "Asia/Hong_Kong",
  "Asia/Taipei", "Asia/Tokyo", "Asia/Seoul",
  "Australia/Perth", "Australia/Adelaide", "Australia/Sydney",
  "Australia/Brisbane", "Australia/Melbourne",
  "Pacific/Auckland", "Pacific/Fiji",
  "Africa/Cairo", "Africa/Johannesburg", "Africa/Lagos", "Africa/Nairobi",
];

function getTimezones(): string[] {
  try {
    // @ts-ignore — supportedValuesOf is available in modern browsers
    const list = Intl.supportedValuesOf("timeZone") as string[];
    return list.length > 0 ? list : COMMON_TZ;
  } catch {
    return COMMON_TZ;
  }
}

const LOCALES = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "en-AU", label: "English (Australia)" },
  { value: "en-CA", label: "English (Canada)" },
  { value: "fr-FR", label: "French (France)" },
  { value: "fr-CA", label: "French (Canada)" },
  { value: "de-DE", label: "German" },
  { value: "es-ES", label: "Spanish (Spain)" },
  { value: "es-MX", label: "Spanish (Mexico)" },
  { value: "pt-BR", label: "Portuguese (Brazil)" },
  { value: "pt-PT", label: "Portuguese (Portugal)" },
  { value: "it-IT", label: "Italian" },
  { value: "nl-NL", label: "Dutch" },
  { value: "pl-PL", label: "Polish" },
  { value: "ru-RU", label: "Russian" },
  { value: "tr-TR", label: "Turkish" },
  { value: "ar-SA", label: "Arabic (Saudi Arabia)" },
  { value: "ja-JP", label: "Japanese" },
  { value: "ko-KR", label: "Korean" },
  { value: "zh-CN", label: "Chinese (Simplified)" },
  { value: "zh-TW", label: "Chinese (Traditional)" },
  { value: "hi-IN", label: "Hindi" },
  { value: "id-ID", label: "Indonesian" },
  { value: "th-TH", label: "Thai" },
  { value: "vi-VN", label: "Vietnamese" },
  { value: "sv-SE", label: "Swedish" },
  { value: "da-DK", label: "Danish" },
  { value: "no-NO", label: "Norwegian" },
  { value: "fi-FI", label: "Finnish" },
];

// ── Profile Tab ──────────────────────────────────────────────────────────────
function ProfileTab({ profile }: { profile: UserProfile }) {
  const { toast } = useToast();
  const timezones = useMemo(() => getTimezones(), []);

  const [form, setForm] = useState({
    fullName: profile.fullName ?? "",
    jobTitle: profile.jobTitle ?? "",
    avatarUrl: profile.avatarUrl ?? "",
    timezone: profile.timezone ?? "",
    locale: profile.locale ?? "",
  });

  const mut = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", "/api/user/me/profile", form).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/me"] });
      toast({ title: "Profile updated", description: "Your changes have been saved." });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><User className="w-4 h-4" /> Profile</CardTitle>
        <CardDescription>Update your personal information visible to admins and teammates.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="fullName">Full name</Label>
            <Input id="fullName" value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="jobTitle">Job title</Label>
            <Input id="jobTitle" value={form.jobTitle}
              onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="avatarUrl">Avatar URL</Label>
            <Input id="avatarUrl" placeholder="https://…" value={form.avatarUrl}
              onChange={(e) => setForm({ ...form, avatarUrl: e.target.value })} />
            {form.avatarUrl && (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <img src={form.avatarUrl} alt="Avatar preview"
                className="w-12 h-12 rounded-full object-cover border mt-1"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={form.timezone || "_none"} onValueChange={(v) => setForm({ ...form, timezone: v === "_none" ? "" : v })}>
              <SelectTrigger id="timezone" className="w-full">
                <SelectValue placeholder="Select timezone…" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="_none">— Not set —</SelectItem>
                {timezones.map((tz) => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="locale">Locale</Label>
            <Select value={form.locale || "_none"} onValueChange={(v) => setForm({ ...form, locale: v === "_none" ? "" : v })}>
              <SelectTrigger id="locale" className="w-full">
                <SelectValue placeholder="Select locale…" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="_none">— Not set —</SelectItem>
                {LOCALES.map((l) => (
                  <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="font-medium">Email:</span>{profile.email}
          <span className="font-medium ml-4">Username:</span>{profile.userName}
        </div>
        <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
          {mut.isPending ? "Saving…" : "Save profile"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Security Tab ─────────────────────────────────────────────────────────────
function SecurityTab({ profile }: { profile: UserProfile }) {
  const { toast } = useToast();
  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [totpCode, setTotpCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [setupData, setSetupData] = useState<{ otpUri: string; secret: string } | null>(null);

  const changePassword = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/user/me/change-password", {
        currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword,
      }).then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.message ?? "Password change failed");
        return d;
      }),
    onSuccess: (d) => {
      setPwForm({ currentPassword: "", newPassword: "", confirm: "" });
      toast({ title: "Password changed", description: d.message });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const startTotp = useMutation({
    mutationFn: () => apiRequest("POST", "/api/user/me/mfa/totp/setup").then((r) => r.json()),
    onSuccess: (d) => setSetupData(d),
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const confirmTotp = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/user/me/mfa/totp/confirm", { code: totpCode }).then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.message ?? "Invalid code");
        return d;
      }),
    onSuccess: (d) => {
      setSetupData(null);
      setTotpCode("");
      setRecoveryCodes(d.recoveryCodes);
      queryClient.invalidateQueries({ queryKey: ["/api/user/me"] });
      toast({ title: "MFA enabled", description: "TOTP MFA is now active." });
    },
    onError: (e: any) => toast({ title: "Invalid code", description: e.message, variant: "destructive" }),
  });

  const disableTotp = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/user/me/mfa/totp/disable", { code: disableCode }).then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.message ?? "Invalid code");
        return d;
      }),
    onSuccess: (d) => {
      setDisableCode("");
      queryClient.invalidateQueries({ queryKey: ["/api/user/me"] });
      toast({ title: "MFA disabled", description: d.message });
    },
    onError: (e: any) => toast({ title: "Invalid code", description: e.message, variant: "destructive" }),
  });

  const regenCodes = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/user/me/mfa/recovery-codes/regenerate").then((r) => r.json()),
    onSuccess: (d) => { setRecoveryCodes(d.recoveryCodes); toast({ title: "Codes regenerated" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // QR code is rendered locally — never sent to any external service.
  // react-qr-code generates an SVG in the browser from the otpauth:// URI.

  return (
    <div className="space-y-6">
      {/* Password change */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Lock className="w-4 h-4" /> Change password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type="password" value={pwForm.currentPassword}
              onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input type="password" value={pwForm.newPassword}
              onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm new password</Label>
            <Input type="password" value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
          </div>
          {pwForm.confirm && pwForm.newPassword !== pwForm.confirm && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Passwords do not match
            </p>
          )}
          <Button
            onClick={() => changePassword.mutate()}
            disabled={changePassword.isPending || !pwForm.currentPassword || !pwForm.newPassword || pwForm.newPassword !== pwForm.confirm}
          >
            {changePassword.isPending ? "Saving…" : "Change password"}
          </Button>
        </CardContent>
      </Card>

      {/* TOTP MFA */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Two-factor authentication (TOTP)
            {profile.mfaEnabled
              ? <Badge variant="default" className="ml-2 bg-green-600 hover:bg-green-600"><CheckCircle2 className="w-3 h-3 mr-1" />Enabled</Badge>
              : <Badge variant="secondary" className="ml-2"><ShieldAlert className="w-3 h-3 mr-1" />Disabled</Badge>}
          </CardTitle>
          <CardDescription>
            Use an authenticator app (Google Authenticator, Authy, 1Password, etc.) to generate one-time codes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!profile.mfaEnabled && !setupData && (
            <Button variant="outline" onClick={() => startTotp.mutate()} disabled={startTotp.isPending}>
              <ShieldCheck className="w-4 h-4 mr-2" />
              {startTotp.isPending ? "Generating…" : "Set up TOTP"}
            </Button>
          )}
          {setupData && (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
              <p className="text-sm font-medium">
                Scan this QR code with your authenticator app:
              </p>
              {setupData && (
                <div className="w-48 h-48 border rounded bg-white p-2 flex items-center justify-center" data-testid="totp-qr-local">
                  <QRCode value={setupData.otpUri} size={176} />
                </div>
              )}
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none">Can't scan? Enter the secret key manually</summary>
                <code className="block mt-2 bg-muted p-2 rounded break-all select-all">{setupData.secret}</code>
              </details>
              <div className="flex gap-2 items-center">
                <Input placeholder="6-digit code" value={totpCode} maxLength={6} inputMode="numeric"
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                  className="w-36" />
                <Button onClick={() => confirmTotp.mutate()} disabled={confirmTotp.isPending || totpCode.length < 6}>
                  {confirmTotp.isPending ? "Verifying…" : "Confirm"}
                </Button>
                <Button variant="ghost" onClick={() => { setSetupData(null); setTotpCode(""); }}>Cancel</Button>
              </div>
            </div>
          )}
          {profile.mfaEnabled && (
            <div className="space-y-3">
              <div className="flex gap-2 items-center">
                <Input placeholder="TOTP code to disable" value={disableCode} maxLength={6} inputMode="numeric"
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                  className="w-48" />
                <Button variant="destructive" onClick={() => disableTotp.mutate()}
                  disabled={disableTotp.isPending || disableCode.length < 6}>
                  {disableTotp.isPending ? "Disabling…" : "Disable MFA"}
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={() => regenCodes.mutate()} disabled={regenCodes.isPending}>
                <RefreshCw className="w-3 h-3 mr-2" />
                {regenCodes.isPending ? "Regenerating…" : "Regenerate recovery codes"}
              </Button>
            </div>
          )}
          {recoveryCodes && (
            <div className="p-4 border rounded-lg bg-yellow-50 dark:bg-yellow-900/20 space-y-2">
              <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 flex items-center gap-1">
                <KeyRound className="w-4 h-4" /> Recovery codes — save these now
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                Each code can only be used once. Store them in a safe place — they won't be shown again.
              </p>
              <div className="grid grid-cols-2 gap-1">
                {recoveryCodes.map((c, i) => (
                  <code key={i} className="text-xs bg-white dark:bg-black/30 px-2 py-1 rounded font-mono">{c}</code>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={() => {
                navigator.clipboard.writeText(recoveryCodes.join("\n"));
                toast({ title: "Copied to clipboard" });
              }}>
                <Copy className="w-3 h-3 mr-2" /> Copy all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Sessions Tab ─────────────────────────────────────────────────────────────
function SessionsTab() {
  const { toast } = useToast();

  const { data: sessions, isLoading, refetch } = useQuery<Session[]>({
    queryKey: ["/api/user/me/sessions"],
    refetchInterval: 60_000,
  });

  const revokeOne = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/user/me/sessions/${id}`),
    onSuccess: () => {
      refetch();
      toast({ title: "Session revoked" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const revokeAll = useMutation({
    mutationFn: () => apiRequest("POST", "/api/user/me/sessions/revoke-all").then((r) => r.json()),
    onSuccess: (d) => {
      refetch();
      toast({ title: "Sessions revoked", description: d.message });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  function formatDate(iso: string) {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium", timeStyle: "short",
      }).format(new Date(iso));
    } catch { return iso; }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Monitor className="w-4 h-4" /> Active sessions</CardTitle>
          <CardDescription>
            All devices currently signed in to your account. Revoke any session you don't recognise.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && <p className="text-sm text-muted-foreground">Loading sessions…</p>}
          {!isLoading && (!sessions || sessions.length === 0) && (
            <p className="text-sm text-muted-foreground">No active sessions found.</p>
          )}
          {sessions && sessions.length > 0 && (
            <div className="divide-y rounded-lg border">
              {sessions.map((s) => (
                <div key={s.id} className="flex items-start justify-between gap-3 p-3">
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">
                        {s.deviceInfo || "Unknown device"}
                      </span>
                      {s.isCurrent && (
                        <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-green-600 hover:bg-green-600 shrink-0">
                          This device
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      {s.ipAddress && (
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" /> {s.ipAddress}
                        </span>
                      )}
                      <span>Last seen: {formatDate(s.lastSeenAt)}</span>
                      <span>Signed in: {formatDate(s.createdAt)}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => revokeOne.mutate(s.id)}
                    disabled={revokeOne.isPending}
                    aria-label={`Revoke session from ${s.deviceInfo || "unknown device"}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><LogOut className="w-4 h-4" /> Revoke all sessions</CardTitle>
          <CardDescription>
            Signs out all devices. Your current session will also be invalidated — you will need to log in again.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="destructive" onClick={() => revokeAll.mutate()} disabled={revokeAll.isPending}>
            <LogOut className="w-4 h-4 mr-2" />
            {revokeAll.isPending ? "Revoking…" : "Revoke all sessions"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Preferences Tab ───────────────────────────────────────────────────────────
const EMAIL_EVENT_LABELS: { key: keyof EmailEvents; label: string; description: string }[] = [
  { key: "tickets",   label: "Tickets",   description: "Ticket assigned, updated, or resolved." },
  { key: "approvals", label: "Approvals", description: "Tool action approval requested or decided." },
  { key: "liveChat",  label: "Live chat", description: "Live chat escalations and offline messages." },
  { key: "billing",   label: "Billing",   description: "Plan changes and payment alerts." },
  { key: "security",  label: "Security",  description: "New login from an unrecognised device or password changes." },
];

function PreferencesTab({ profile }: { profile: UserProfile }) {
  const { toast } = useToast();
  const prefs = profile.preferences ?? {};
  const defaultEmailEvents: EmailEvents = {
    tickets: true, approvals: true, liveChat: true, billing: true, security: true,
  };
  const [form, setForm] = useState({
    theme: prefs.theme ?? "system",
    density: prefs.density ?? "comfortable",
    emailNotifications: prefs.emailNotifications ?? true,
    browserNotifications: prefs.browserNotifications ?? false,
    emailEvents: { ...defaultEmailEvents, ...(prefs.emailEvents ?? {}) } as EmailEvents,
  });

  const mut = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", "/api/user/me/preferences", {
        theme: form.theme,
        density: form.density,
        emailNotifications: form.emailNotifications,
        browserNotifications: form.browserNotifications,
        emailEvents: form.emailEvents,
      }).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/me"] });
      toast({ title: "Preferences saved" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  function setEmailEvent(key: keyof EmailEvents, val: boolean) {
    setForm((f) => ({ ...f, emailEvents: { ...f.emailEvents, [key]: val } }));
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Monitor className="w-4 h-4" /> Appearance</CardTitle>
          <CardDescription>Control how the application looks on this device.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Theme</Label>
            <Select value={form.theme} onValueChange={(v) => setForm({ ...form, theme: v })}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="system">System default</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Display density</Label>
            <Select value={form.density} onValueChange={(v) => setForm({ ...form, density: v })}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="comfortable">Comfortable</SelectItem>
                <SelectItem value="compact">Compact</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Compact reduces spacing throughout the interface.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Bell className="w-4 h-4" /> Notifications</CardTitle>
          <CardDescription>Choose which events trigger email and browser notifications.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Global email toggle */}
          <div className="space-y-3">
            <p className="text-sm font-medium">Email notifications</p>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="emailNotif" className="font-normal">Enable email notifications</Label>
                <p className="text-xs text-muted-foreground">Master toggle — disabling this overrides all event preferences below.</p>
              </div>
              <Switch id="emailNotif" checked={form.emailNotifications}
                onCheckedChange={(v) => setForm({ ...form, emailNotifications: v })} />
            </div>

            {/* Per-event type toggles */}
            {form.emailNotifications && (
              <div className="rounded-lg border divide-y ml-0">
                {EMAIL_EVENT_LABELS.map(({ key, label, description }) => (
                  <div key={key} className="flex items-center justify-between px-3 py-2.5 gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{description}</p>
                    </div>
                    <Switch
                      id={`email-event-${key}`}
                      checked={form.emailEvents[key] ?? true}
                      onCheckedChange={(v) => setEmailEvent(key, v)}
                      aria-label={label}
                    />
                  </div>
                ))}
              </div>
            )}
            {!form.emailNotifications && (
              <p className="text-xs text-muted-foreground ml-0">
                All email notifications are currently disabled.
              </p>
            )}
          </div>

          {/* Browser notifications */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Browser notifications</p>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="browserNotif" className="font-normal">Enable browser push notifications</Label>
                <p className="text-xs text-muted-foreground">Requires browser permission to be granted.</p>
              </div>
              <Switch id="browserNotif" checked={form.browserNotifications}
                onCheckedChange={async (v) => {
                  if (v && typeof Notification !== "undefined" && Notification.permission !== "granted") {
                    const result = await Notification.requestPermission();
                    if (result !== "granted") {
                      toast({
                        title: "Permission denied",
                        description: "Enable notifications in your browser's site settings and reload.",
                        variant: "destructive",
                      });
                      return;
                    }
                  }
                  setForm({ ...form, browserNotifications: v });
                }} />
            </div>
            {typeof Notification !== "undefined" && Notification.permission === "denied" && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Blocked by browser — update site settings and reload to enable.
              </p>
            )}
            {typeof Notification !== "undefined" && Notification.permission === "granted" && form.browserNotifications && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Permission granted.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
        {mut.isPending ? "Saving…" : "Save preferences"}
      </Button>
      <p className="text-xs text-muted-foreground">
        Email delivery infrastructure status: notification preference logic is enforced server-side.
        Actual inbox delivery depends on the platform's email provider configuration.
      </p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function UserSettingsPage() {
  const { data: profile, isLoading, isError } = useQuery<UserProfile>({
    queryKey: ["/api/user/me"],
  });

  if (isLoading) return <PageShell title="My Settings"><p className="text-sm text-muted-foreground">Loading…</p></PageShell>;
  if (isError || !profile) return <PageShell title="My Settings"><p className="text-sm text-destructive">Failed to load profile.</p></PageShell>;

  return (
    <PageShell
      title="My Settings"
      subtitle="Manage your personal profile, security, and preferences."
    >
      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList>
          <TabsTrigger value="profile"><User className="w-3.5 h-3.5 mr-1.5" />Profile</TabsTrigger>
          <TabsTrigger value="security"><Lock className="w-3.5 h-3.5 mr-1.5" />Security</TabsTrigger>
          <TabsTrigger value="sessions"><Monitor className="w-3.5 h-3.5 mr-1.5" />Sessions</TabsTrigger>
          <TabsTrigger value="preferences"><Bell className="w-3.5 h-3.5 mr-1.5" />Preferences</TabsTrigger>
        </TabsList>

        <TabsContent value="profile"><ProfileTab profile={profile} /></TabsContent>
        <TabsContent value="security"><SecurityTab profile={profile} /></TabsContent>
        <TabsContent value="sessions"><SessionsTab /></TabsContent>
        <TabsContent value="preferences"><PreferencesTab profile={profile} /></TabsContent>
      </Tabs>
    </PageShell>
  );
}
